'use client';

import { joinClassNames, WithClassnames } from '@vertex-protocol/web-common';
import { getResolvedColorValue } from 'client/modules/theme/colorVars';
import {
  BarData,
  CandlestickData,
  CandlestickSeries,
  createChart,
  HistogramData,
  HistogramSeries,
  IChartApi,
  IPriceLine,
  ISeriesApi,
  LineStyle,
  MouseEventParams,
  Time,
} from 'lightweight-charts';
import { OrderPriceLineData } from 'client/modules/trading/chart/getOrderPriceLinesFromEngineOrders';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface CandleData {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

interface OhlcHeaderData {
  open: number;
  high: number;
  low: number;
  close: number;
  change: number;
  changePct: number;
}

interface Props extends WithClassnames {
  candles?: CandleData[];
  productId?: number;
  orderPriceLines?: OrderPriceLineData[];
  /** Market name shown in the OHLC header, e.g. "BTC-PERP" */
  symbol?: string;
  /** Human-readable timeframe label, e.g. "1h", "4h", "1d" */
  period?: string;
}

// Fallbacks mirror the ArCa light theme when CSS variables have not resolved yet.
const CHART_COLORS = {
  background: getResolvedColorValue('surface-card') || 'rgb(255, 255, 255)',
  text: getResolvedColorValue('text-primary') || '#0E1020',
  grid: getResolvedColorValue('stroke') || '#D8D4C7',
  border: getResolvedColorValue('stroke') || '#D8D4C7',
  primary: getResolvedColorValue('accent') || '#B8922A',
  positive: getResolvedColorValue('positive') || '#2E7D58',
  negative: getResolvedColorValue('negative') || '#B84A4A',
};

// Exact Nado TradingView colors for order labels
const ORDER_LABEL_COLORS = {
  buyBg: 'rgba(76, 175, 80, 0.85)',
  buyText: '#F8F6ED',
  sellBg: 'rgba(255, 82, 82, 0.85)',
  sellText: '#F8F6ED',
  buyLine: '#4CAF50',
  sellLine: '#FF5252',
};

interface LabelPosition {
  price: number;
  y: number;
  side: 'buy' | 'sell';
  title: string;
}

export function LightweightChart({
  className,
  candles = [],
  orderPriceLines = [],
  symbol,
  period = '1h',
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const priceLineRefs = useRef<IPriceLine[]>([]);
  const [labelPositions, setLabelPositions] = useState<LabelPosition[]>([]);
  const [ohlcHeader, setOhlcHeader] = useState<OhlcHeaderData | null>(null);

  // Recalculate label Y positions from price coordinates
  const updateLabelPositions = useCallback(() => {
    const series = seriesRef.current;
    if (!series || !orderPriceLines.length) {
      setLabelPositions([]);
      return;
    }

    const positions: LabelPosition[] = [];
    orderPriceLines.forEach((order) => {
      const y = series.priceToCoordinate(order.price);
      if (y !== null && y !== undefined) {
        positions.push({
          price: order.price,
          y: y as number,
          side: order.side,
          title: order.title,
        });
      }
    });
    setLabelPositions(positions);
  }, [orderPriceLines]);

  useEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      layout: {
        background: { color: CHART_COLORS.background },
        textColor: CHART_COLORS.text,
      },
      grid: {
        vertLines: { color: CHART_COLORS.grid },
        horzLines: { color: CHART_COLORS.grid },
      },
      crosshair: {
        vertLine: {
          color: CHART_COLORS.primary,
          labelBackgroundColor: CHART_COLORS.primary,
        },
        horzLine: {
          color: CHART_COLORS.primary,
          labelBackgroundColor: CHART_COLORS.primary,
        },
      },
      rightPriceScale: {
        borderColor: CHART_COLORS.border,
      },
      timeScale: {
        borderColor: CHART_COLORS.border,
        timeVisible: true,
        secondsVisible: false,
      },
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight || 400,
    });

    const candlestickSeries = chart.addSeries(CandlestickSeries, {
      upColor: CHART_COLORS.positive,
      downColor: CHART_COLORS.negative,
      borderUpColor: CHART_COLORS.positive,
      borderDownColor: CHART_COLORS.negative,
      wickUpColor: CHART_COLORS.positive,
      wickDownColor: CHART_COLORS.negative,
    });

    // Volume histogram — pinned to the bottom 20% of the chart area
    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
    });
    chart.priceScale('volume').applyOptions({
      scaleMargins: { top: 0.8, bottom: 0 },
    });

    chartRef.current = chart;
    seriesRef.current = candlestickSeries;
    volumeSeriesRef.current = volumeSeries;

    const handleResize = () => {
      if (containerRef.current) {
        chart.applyOptions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight || 400,
        });
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      volumeSeriesRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!seriesRef.current) return;

    if (!candles.length) {
      seriesRef.current.setData([]);
      volumeSeriesRef.current?.setData([]);
      setOhlcHeader(null);
      return;
    }

    const sorted = candles.slice().sort((a, b) => a.time - b.time);

    const chartData: CandlestickData<Time>[] = sorted.map((c) => ({
      time: (c.time / 1000) as Time,
      open: c.open,
      high: c.high,
      low: c.low,
      close: c.close,
    }));

    seriesRef.current.setData(chartData);

    // Volume bars: green when close >= open, red when close < open, semi-transparent
    if (volumeSeriesRef.current) {
      const volumeData: HistogramData<Time>[] = sorted
        .filter((c) => c.volume !== undefined && c.volume > 0)
        .map((c) => ({
          time: (c.time / 1000) as Time,
          value: c.volume as number,
          color:
            c.close >= c.open
              ? 'rgba(61, 245, 123, 0.4)'
              : 'rgba(234, 67, 92, 0.4)',
        }));
      volumeSeriesRef.current.setData(volumeData);
    }

    if (chartRef.current) {
      chartRef.current.timeScale().fitContent();
    }

    // Show latest candle in OHLC header by default
    const latest = chartData[chartData.length - 1];
    if (latest) {
      const change = latest.close - latest.open;
      const changePct = latest.open !== 0 ? (change / latest.open) * 100 : 0;
      setOhlcHeader({
        open: latest.open,
        high: latest.high,
        low: latest.low,
        close: latest.close,
        change,
        changePct,
      });
    }
  }, [candles]);

  // Subscribe to crosshair moves to update the OHLC header with hovered candle
  useEffect(() => {
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!chart || !series) return;

    const handler = (param: MouseEventParams<Time>) => {
      if (!param.time || !param.seriesData) {
        // Crosshair left the chart — revert to latest candle
        if (candles.length) {
          const sorted = candles.slice().sort((a, b) => a.time - b.time);
          const latest = sorted[sorted.length - 1];
          const change = latest.close - latest.open;
          const changePct = latest.open !== 0 ? (change / latest.open) * 100 : 0;
          setOhlcHeader({ open: latest.open, high: latest.high, low: latest.low, close: latest.close, change, changePct });
        }
        return;
      }

      const bar = param.seriesData.get(series) as BarData<Time> | undefined;
      if (!bar) return;

      const change = bar.close - bar.open;
      const changePct = bar.open !== 0 ? (change / bar.open) * 100 : 0;
      setOhlcHeader({
        open: bar.open,
        high: bar.high,
        low: bar.low,
        close: bar.close,
        change,
        changePct,
      });
    };

    chart.subscribeCrosshairMove(handler);

    return () => {
      chart.unsubscribeCrosshairMove(handler);
    };
  // Re-subscribe when candles change so the "revert to latest" closure is fresh
  }, [candles]);

  // Manage limit order price lines
  useEffect(() => {
    const series = seriesRef.current;
    const chart = chartRef.current;
    if (!series) return;

    // Remove all existing price lines
    priceLineRefs.current.forEach((line) => {
      series.removePriceLine(line);
    });
    priceLineRefs.current = [];

    // Add dashed lines (no title text, axis label shows price)
    orderPriceLines.forEach((order) => {
      const lineColor = order.side === 'buy' ? ORDER_LABEL_COLORS.buyLine : ORDER_LABEL_COLORS.sellLine;
      const line = series.createPriceLine({
        price: order.price,
        color: lineColor,
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: '',
      });
      priceLineRefs.current.push(line);
    });

    // Update HTML label positions
    updateLabelPositions();

    // Subscribe to chart crosshair/scale changes to reposition labels
    const unsubCrosshair = chart?.subscribeCrosshairMove(() => {
      updateLabelPositions();
    });

    return () => {
      const currentSeries = seriesRef.current;
      if (currentSeries) {
        priceLineRefs.current.forEach((line) => {
          currentSeries.removePriceLine(line);
        });
        priceLineRefs.current = [];
      }
      if (unsubCrosshair) {
        // subscribeCrosshairMove returns void in v5, unsubscribe not needed
      }
    };
  }, [orderPriceLines, updateLabelPositions]);

  // Also update labels when chart rescales (zoom/scroll)
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const timeScale = chart.timeScale();
    const handler = () => updateLabelPositions();
    timeScale.subscribeVisibleLogicalRangeChange(handler);

    return () => {
      timeScale.unsubscribeVisibleLogicalRangeChange(handler);
    };
  }, [updateLabelPositions]);

  return (
    <div className={joinClassNames('relative h-full w-full overflow-hidden', className)}>
      <div ref={containerRef} className="h-full w-full" />
      {/* OHLC data header overlay — top-left of chart area */}
      {ohlcHeader && (
        <div
          className="pointer-events-none absolute left-2 top-2 flex items-center gap-x-1.5 font-mono text-[11px] leading-none"
          style={{ zIndex: 10 }}
        >
          {symbol && (
            <span className="font-sans font-semibold text-text-primary">
              {symbol} · {period} · ArCa
            </span>
          )}
          <span className="text-text-tertiary">
            O<span className="text-text-primary">
              {ohlcHeader.open.toPrecision(6)}
            </span>
          </span>
          <span className="text-text-tertiary">
            H<span className="text-text-primary">
              {ohlcHeader.high.toPrecision(6)}
            </span>
          </span>
          <span className="text-text-tertiary">
            L<span className="text-text-primary">
              {ohlcHeader.low.toPrecision(6)}
            </span>
          </span>
          <span className="text-text-tertiary">
            C<span className="text-text-primary">
              {ohlcHeader.close.toPrecision(6)}
            </span>
          </span>
          <span
            style={{
              color:
                ohlcHeader.change >= 0
                  ? CHART_COLORS.positive
                  : CHART_COLORS.negative,
            }}
          >
            {ohlcHeader.change >= 0 ? '+' : ''}
            {ohlcHeader.change.toPrecision(4)} ({ohlcHeader.change >= 0 ? '+' : ''}
            {ohlcHeader.changePct.toFixed(2)}%)
          </span>
        </div>
      )}
      {/* Left-side order labels overlay */}
      {labelPositions.map((label, i) => {
        const isBuy = label.side === 'buy';
        return (
          <div
            key={`${label.price}-${i}`}
            className="pointer-events-none absolute left-0 flex items-center"
            style={{
              top: label.y - 10,
              zIndex: 5,
            }}
          >
            <div
              className="flex items-center gap-1 whitespace-nowrap rounded-r px-1.5 py-0.5 text-[10px] font-medium leading-tight"
              style={{
                backgroundColor: isBuy ? ORDER_LABEL_COLORS.buyBg : ORDER_LABEL_COLORS.sellBg,
                color: isBuy ? ORDER_LABEL_COLORS.buyText : ORDER_LABEL_COLORS.sellText,
              }}
            >
              {label.title}
            </div>
          </div>
        );
      })}
    </div>
  );
}
